import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { EditableComponentName, FieldType, ModuleConfig, ModuleField, useEditor } from '@toasttab/sites-components';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import { Image as ImageObject, LogoSizeOption } from 'src/apollo/sites';
import useOnScroll from 'src/lib/js/hooks/useOnScroll';
import { ScrollDirection } from 'src/lib/js/hooks/useScrollDirection';

import GlobalNav, { NavType } from 'shared/components/common/nav/Nav';
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext';
import { EditablePrimaryCTA, PrimaryCTAClasses } from 'shared/components/primary_cta/PrimaryCTA';
import { SiteData } from 'shared/js/types';

import { AdditionalCTAData, CTA, CTAData } from 'public/components/default_template/ctas';
import CTAItem from 'public/components/default_template/ctas/CTAItem';
import CartModal, { CartModalIconButton } from 'public/components/default_template/online_ordering/cart/CartModal';
import SpotlightBanner, { getConsolidatedBanners, showSpotlight } from 'public/components/default_template/spotlight/SpotlightBanner';
import { useSpotlightContext } from 'public/components/default_template/spotlight/SpotlightContext';
import { useShouldShowCartInNavigation } from 'public/components/pages/WrapWithCartRelatedProviders';

import { ResponsiveNavItems } from './NavItems';
import NavLogo from './NavLogo';
import UserNav from './UserNav';
import { getNavColorStyles, getNavOpacity } from './navUtils';

export type CommonNavProps = {
  shouldShowPreviewBanner: boolean;
  cartDisabled?: boolean;
  className?: string;
  logoSize?: LogoSizeOption | null;
  logoSrc: string;
  logoObject?: ImageObject | null;
  navType?: NavType | null;
  nonPrimaryCtas?: CTAData[] | null;
  withCart?: boolean;
  withOOOptions?: boolean;
  withShadow?: boolean;
  // certain pages (online ordering) should always display toast login
  forceDisplayToastLogin?: boolean;
};

type NavContentProps = CommonNavProps & {
  restaurant?: SiteData;
  scrollDirection?: ScrollDirection;
  ctaProps?: AdditionalCTAData;
  primaryCtaContent?: CTA | null;
  setDisplayingOverlay?: (d: boolean) => void;
  logoLink?: string;
  logoLinkSameTab?: boolean;
};

type WrappedNavContentProps = NavContentProps & {
  route: string;
};

type PrimaryCTAFactory = ({ excludeCart }: {
  excludeCart?: boolean
}) => JSX.Element | null

export const getNavSchema = (restaurant: SiteData | undefined, shouldShowCartInNavigation: boolean = false) => {
  const style = getNavColorStyles(restaurant, true);
  const navConfig = restaurant?.content?.navConfig;
  const navLayout = navConfig?.navLayout || 'header-layout-1';
  const [bgColorPath, textColorPath] = restaurant?.theme?.enabled
    ? ['theme.colorOverrides.navigation.background', 'theme.colorOverrides.navigation.text']
    : ['content.navConfig.backgroundColor', 'content.navConfig.textColor'];
  const navSchema = {
    name: 'navigation bar' as EditableComponentName,
    displayName: 'Nav Bar',
    actions: [],
    schema: {
      fields: [
        {
          displayName: 'Background color',
          path: bgColorPath,
          type: FieldType.BackgroundColor,
          value: style.backgroundColor
        },
        {
          displayName: 'Text color',
          path: textColorPath,
          type: FieldType.Color,
          value: style.color
        },
        {
          displayName: 'Opacity',
          path: 'content.navConfig.opacity',
          type: FieldType.Slider,
          value: navConfig?.opacity
        },
        {
          displayName: 'Overlaid',
          path: 'content.navConfig.overlaid',
          type: FieldType.Boolean,
          value: navConfig?.overlaid
        },
        {
          displayName: 'Sticky',
          path: 'content.navConfig.sticky',
          type: FieldType.Boolean,
          value: navConfig?.sticky
        },
        {
          displayName: 'Primary CTA',
          path: 'content.primaryCta',
          type: FieldType.Boolean,
          value: !!restaurant?.content?.primaryCta,
          activeValue: {
            nav: true,
            text: 'View Menu',
            type: 'menu',
            link: '/menu',
            submenus: []
          }
        },
        // the alwaysShowPrimaryCTA should only show if it is a hamburger nav layout and if primaryCTA is on
        ... !!restaurant?.content?.primaryCta && isHamburgerAlwaysVisibleInLayout(navLayout) ?
          [
            {
              displayName: 'Always Show Primary CTA',
              path: 'content.navConfig.alwaysShowPrimaryCTA',
              type: FieldType.Boolean,
              value: navConfig?.alwaysShowPrimaryCTA && !!restaurant?.content?.primaryCta
            } as ModuleField
          ]
          : [],
        {
          displayName: 'Show Toast Login',
          path: 'content.navConfig.showToastLogin',
          type: FieldType.Boolean,
          value: navConfig?.showToastLogin ?? true
        }
      ]
    } as ModuleConfig
  };

  if(shouldShowCartInNavigation) {
    navSchema.schema.fields.push({
      displayName: 'Show Cart',
      path: 'content.navConfig.showCartInNavigation',
      type: FieldType.Boolean,
      value: navConfig?.showCartInNavigation ?? false
    } as ModuleField);
  }
  return navSchema;
};

const WrappedNavContent = (props: React.PropsWithChildren<WrappedNavContentProps>) => {
  const { useEditableRef, isEditor } = useEditor();
  // After the logo loads, rerender the parent of ResponsiveNavItems
  // so that ResponsiveNavItems will resize to fit the screen
  const [logoLoaded, setLogoLoaded] = useState(false);
  const onLogoLoad = useCallback(() => setLogoLoaded(true), []);
  const [scrolledToTop, setScrolledToTop] = useState(true);
  const { isHidden } = useSpotlightContext();
  const { toastProduct, ooRestaurant, currentPageType, restaurant: { config: { isOnlineOrderingEnabled } } } = useRestaurant();
  const shouldShowCartInNavigation = useShouldShowCartInNavigation(currentPageType);
  let style = getNavColorStyles(props.restaurant, true);
  const hamburgerStyle = { fill: style.color };
  const navConfig = props.restaurant?.content?.navConfig;
  let navType = props.navType;
  if(!navType) {
    if(navConfig?.overlaid) {
      if(navConfig?.sticky === false) {
        navType = 'absoluteNav';
      } else {
        navType = 'fixedNav';
      }
    } else if(navConfig?.sticky === false) {
      navType = 'normal';
    } else {
      navType = 'stickyNav';
    }
  }
  const navLayout = navConfig?.navLayout || 'header-layout-1';

  const scrollReponsiveNav = navType === 'fixedNav' || navType === 'stickyNav';
  if(scrollReponsiveNav && !scrolledToTop && getNavOpacity(navConfig) === 0) {
    const stylesWithoutOpacity = getNavColorStyles(props.restaurant, false);
    style.backgroundColor = stylesWithoutOpacity.backgroundColor;
  }

  const toggleMenu = useCallback(() => {
    // toggle the menu
    props.setDisplayingOverlay!(true);
  }, [props.setDisplayingOverlay]);

  // to make the hamburger more accessible for keyboard access
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if(e.key === ' ' || e.key === 'Spacebar' || e.key === 'Enter') {
      e.preventDefault(); // Prevent the default space key action (page scroll)
      toggleMenu();
    }
  }, [toggleMenu]);

  const debouncedScrollUpdate = debounce(() => setScrolledToTop(window.scrollY === 0), 25);
  useOnScroll(() => debouncedScrollUpdate());

  const navSchema = useMemo(() => {
    return getNavSchema(props.restaurant, shouldShowCartInNavigation);
  }, [props.restaurant, shouldShowCartInNavigation]);
  const { editableRef: editableNavBarRef, pushSchema } = useEditableRef<HTMLDivElement>(navSchema);
  useEffect(() => {
    // resubmit the changed schema when primaryCTA toggle changes.
    pushSchema();
  }, [pushSchema, props.restaurant?.content?.primaryCta]);
  const { editableRef: editableNavListRef } = useEditableRef<HTMLDivElement>({ name: 'navigation list', displayName: 'Nav List', actions: [], schema: { fields: [] } });
  const { editableRef: editableHamburgerRef } = useEditableRef<HTMLButtonElement>({ name: 'navigation list', displayName: 'Nav List', actions: [], schema: { fields: [] } });

  const showFixedTopSpotlight = showSpotlight(
    { fixed: true },
    props?.restaurant?.content?.spotlightBanner,
    ooRestaurant?.spotlightConfig,
    getConsolidatedBanners(props.restaurant?.content?.spotlightBanner, ooRestaurant?.spotlightConfig),
    `/${props.route.split('/')[1]}`,
    toastProduct
  );

  const hamburger =
    props.nonPrimaryCtas?.length && props.setDisplayingOverlay
      ?
      <li className={classnames('hamburgerWrapper', { 'hidden-md-up': !isHamburgerAlwaysVisibleInLayout(navLayout) })}>
        <button
          className={classnames('hamburger', { 'hidden-md-up': !isHamburgerAlwaysVisibleInLayout(navLayout) })}
          onClick={() => toggleMenu()}
          onKeyDown={handleKeyDown}
          nav-role="hamburger-button"
          data-testid="hamburger-button"
          tabIndex={0}
          aria-label={'hamburger menu'}
          aria-description="click to open menu"
          ref={editableHamburgerRef}>
          <svg viewBox="0 0 100 80" width="20" height="20" {...hamburgerStyle}>
            <rect width="100" height="15"></rect>
            <rect y="30" width="100" height="15"></rect>
            <rect y="60" width="100" height="15"></rect>
          </svg>
        </button>
      </li>
      : null;

  const logo =
    <li data-testid="logoLink" nav-role="logo">
      <NavLogo
        logoLink={props.logoLink}
        logoLinkSameTab={props.logoLinkSameTab}
        style={style}
        onLoad={onLogoLoad}
        logoSrc={props.logoSrc}
        logoObject={props.logoObject}
        logoSize={props.logoSize || navConfig?.logoSize}
        scrollDirection={scrollReponsiveNav ? props.scrollDirection : undefined} />
    </li>;

  const links = props.ctaProps
    ?
    <li role="none" className={classnames('hidden-sm-down', { center: props.withOOOptions })} nav-role="links">
      <div role="none" className="navLinks" ref={editableNavListRef} key={`nav-links-${logoLoaded}`}>
        <ResponsiveNavItems ctas={props.nonPrimaryCtas} ctaProps={props.ctaProps} parentRef={editableNavListRef} moreMenuLabel={navConfig?.moreMenuLabel} color={navConfig?.textColor} />
      </div>
    </li>
    : null;
  const canShowCartInNavigation = shouldShowCartInNavigation && navConfig?.showCartInNavigation && isOnlineOrderingEnabled;

  const primaryCTA: PrimaryCTAFactory = useCallback(({ excludeCart = false }) => {
    const showCartModal = props.withCart && !excludeCart || canShowCartInNavigation;
    return (
      <ul role="none" nav-role="primary-cta" >
        {isEditor && showCartModal ? <li><CartModalIconButton /></li> : null}
        {!isEditor && showCartModal ? <li><CartModal disabled={props.cartDisabled} /></li> : null}
        {props.primaryCtaContent ?
          <li className="cta hidden-sm-down">
            <CTAItem cta={props.primaryCtaContent} isPrimary className={PrimaryCTAClasses} editPath="content.primaryCta">
              <EditablePrimaryCTA editPath="content.primaryCta" href={props.primaryCtaContent?.link} text={props.primaryCtaContent?.text} type={props.primaryCtaContent.type} />
            </CTAItem>
          </li>
          : null}
        <li><UserNav forceDisplayToastLogin={props.forceDisplayToastLogin} /></li>
      </ul>);
  }, [props.cartDisabled, props.primaryCtaContent, props.withCart, props.forceDisplayToastLogin, isEditor, canShowCartInNavigation]);

  const ooOptions = props.withOOOptions || props.withCart || canShowCartInNavigation
    ?
    <ul nav-role="primary-cta">
      {isEditor && (props.withCart || canShowCartInNavigation ) ? <CartModalIconButton /> : null}
      {!isEditor && (props.withCart || canShowCartInNavigation ) ? <CartModal disabled={props.cartDisabled} /> : null}
    </ul>
    : null;

  const alwaysPrimaryCTA: PrimaryCTAFactory = useCallback(({ excludeCart = false }) =>
    navConfig?.alwaysShowPrimaryCTA && !!props.restaurant?.content?.primaryCta ?
      <div data-testid="alwaysShowPrimaryCta">{primaryCTA({ excludeCart })}</div> :
      null,
  [navConfig?.alwaysShowPrimaryCTA, primaryCTA, props.restaurant?.content?.primaryCta]);
  const navChildren = organizeNavChildren(
    {
      hamburger,
      logo,
      links,
      primaryCTA,
      ooOptions,
      alwaysPrimaryCTA
    },
    navLayout
  );
  return (
    <>
      {navType === 'stickyNav' && <SpotlightBanner fixed={false} />}
      {!scrollReponsiveNav && <SpotlightBanner fixed={true} />}
      <GlobalNav navType={navType}
        shouldShowPreviewBanner={props.shouldShowPreviewBanner}
        className={classnames(props.className, { spotlightOffset: showFixedTopSpotlight && !isHidden })}>
        {navType !== 'stickyNav' && <SpotlightBanner fixed={false} style={navType === 'fixedNav' && !scrolledToTop ? { height: 0 } : {}} />}
        {scrollReponsiveNav && <SpotlightBanner fixed={true} />}
        <div className={classnames('navWrapper', { withShadow: props.withShadow })} style={style} ref={editableNavBarRef}>
          <div className={classnames('nav', navLayout)}>{navChildren}</div>
        </div>
        {props.children}
      </GlobalNav>
    </>
  );
};

const EditorNavContent = (props: NavContentProps) => {
  const { url } = useEditor();
  return <WrappedNavContent {...props} route={url} />;
};

const SitesNavContent = (props: NavContentProps) => {
  const { pathname } = useLocation();
  return <WrappedNavContent {...props} route={pathname} />;
};

const NavContent = (props: NavContentProps) => {
  const { isEditor } = useEditor();
  return isEditor ? <EditorNavContent {...props} /> : <SitesNavContent {...props} />;
};

export const isHamburgerAlwaysVisibleInLayout = (layout: string): boolean => {
  switch(layout) {
    case 'header-layout-3':
    case 'header-layout-6':
    case 'header-layout-7':
      return true;
    default:
      return false;
  }
};


type NavChildrenType = {
  logo: ReactNode;
  hamburger: ReactNode;
  primaryCTA: PrimaryCTAFactory;
  links: ReactNode;
  ooOptions: ReactNode;
  alwaysPrimaryCTA: PrimaryCTAFactory;
};

export const organizeNavChildren = ({ hamburger, logo, links, primaryCTA, ooOptions, alwaysPrimaryCTA }: NavChildrenType, layout: string): ReactNode => {
  switch(layout) {
    case 'header-layout-8':
      return (
        <>
          <ul className="left">
            {hamburger}
          </ul>
          <ul className="middle">
            {logo}
            {links}
          </ul>
          <div role="none" className="right">{primaryCTA({})}</div>
        </>
      );
    case 'header-layout-7':
      return (
        <>
          <div role="none" className="left"> </div>
          <ul className="middle">{logo}</ul>
          <ul className="right">
            {ooOptions}
            {alwaysPrimaryCTA({ excludeCart: true })}
            {hamburger}
          </ul>
        </>
      );
    case 'header-layout-6':
      return (
        <>
          <ul className="left">
            {hamburger}
            {alwaysPrimaryCTA({ excludeCart: true })}
          </ul>
          <ul className="middle">{logo}</ul>
          <div role="none" className="right">{ooOptions}</div>
        </>
      );
    case 'header-layout-5':
      return (
        <>
          <div role="none" className="left"> </div>
          <ul className="middle">{logo}</ul>
          <div role="none" className="right">
            <ul>{links}</ul>
            {primaryCTA({})}
            <ul>{hamburger}</ul>
          </div>
        </>
      );
    case 'header-layout-4':
      return (
        <>
          <ul className="left">
            {hamburger}
            {links}
          </ul>
          <ul className="middle">{logo}</ul>
          <div role="none" className="right">{primaryCTA({})}</div>
        </>
      );
    case 'header-layout-3':
      return (
        <>
          <ul className="left">{logo}</ul>
          <div role="none" className="middle"></div>
          <ul className="right">
            {ooOptions}
            {alwaysPrimaryCTA({ excludeCart: true })}
            {hamburger}
          </ul>
        </>
      );
    case 'header-layout-2':
    case 'header-layout-1':
    default:
      return (
        <>
          <ul className="left">
            {hamburger}
            {logo}
          </ul>
          <ul className="middle">{links}</ul>
          <div role="none" className="right">{primaryCTA({})}</div>
        </>
      );
  }
};

export default NavContent;
